"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.SortableHeader = SortableHeader;
exports.Tbody = Tbody;
exports.Td = Td;
exports.Tfoot = Tfoot;
exports.Th = Th;
exports.Thead = Thead;
exports.Tr = Tr;
exports.default = Table;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireWildcard(require("react"));
var _classnames = _interopRequireDefault(require("classnames"));
var _arrowDown = _interopRequireDefault(require("@ux/icon/arrow-down"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _text = require("@ux/text");
const _excluded = ["id", "className", "children", "size", "stretch"],
  _excluded2 = ["className", "children", "align"],
  _excluded3 = ["className", "children", "align", "ariaSort"],
  _excluded4 = ["className", "children"],
  _excluded5 = ["className", "children"],
  _excluded6 = ["className", "children"],
  _excluded7 = ["className", "children", "isSticky"],
  _excluded8 = ["className", "children"];
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const SizeContext = /*#__PURE__*/(0, _react.createContext)(null);

/**
 * Creates a Table component
 * @param {Object} props - Properties for the Table element
 * @param {String} props.id - Assigns title to component for a11y
 * @param {String} props.className - Class name applied to component
 * @param {String} props.size - Determines the padding and font size of table content
 * @param {Boolean} props.stretch - Determines whether the table element and its contents should stretch to fit its container
 * @returns {React.ReactComponentElement} - Table component
 */
function Table(_ref) {
  let {
      id,
      className,
      children,
      size = 'md',
      stretch = false
    } = _ref,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  const scrollBoxRef = (0, _react.useRef)(null);
  const targetRef = (0, _react.useRef)(null);
  const computeHeight = (0, _react.useCallback)(function _computeHeight() {
    const [scrollBox, target] = [scrollBoxRef.current, targetRef.current];
    const clientHeight = window.innerHeight || document.documentElement.clientHeight;
    if (scrollBox && target) {
      const targetRect = target.getBoundingClientRect();
      scrollBox.style.setProperty('--amountToMoveThead', `${Math.max(0, -targetRect.y)}px`);
      if (targetRect.bottom > clientHeight) {
        scrollBox.style.height = `${clientHeight - targetRect.top}px`;
      } else {
        scrollBox.style.height = 'auto';
      }
    }
  }, [scrollBoxRef, targetRef]);
  const onIntersection = (0, _react.useCallback)(function _onIntersection(entries) {
    for (const {
      isIntersecting
    } of entries) {
      if (isIntersecting) {
        window.addEventListener('scroll', computeHeight);
        window.addEventListener('resize', computeHeight);
      } else {
        window.removeEventListener('scroll', computeHeight);
        window.removeEventListener('resize', computeHeight);
      }
    }
  }, [computeHeight]);
  (0, _react.useEffect)(function _setUpIntersectionObserver() {
    const target = targetRef.current;
    if (target) {
      const observer = new IntersectionObserver(onIntersection);
      observer.observe(target);
    }
  }, [targetRef, onIntersection]);
  return /*#__PURE__*/_react.default.createElement(SizeContext.Provider, {
    value: size
  }, /*#__PURE__*/_react.default.createElement("div", {
    id: id,
    className: (0, _classnames.default)('ux-table-scrollbox', className),
    "data-version": "2400",
    ref: scrollBoxRef
  }, /*#__PURE__*/_react.default.createElement("table", (0, _extends2.default)({
    className: "ux-table",
    "data-stretch": stretch,
    "data-density": size,
    children
  }, rest, {
    ref: targetRef
  }))));
}

/**
 * Creates a Td component comprised of a html td element
 * @param {Object} props - Properties for the Td element
 * @param {String} props.id - Assigns title to component for a11y
 * @param {String} props.className - Class name applied to component
 * @param {String} props.align - Determines the alignment of content respective to its cell container
 * @returns {React.ReactComponentElement} - Td component
 */
function Td(_ref2) {
  var _useContext;
  let {
      className,
      children,
      align = 'start'
    } = _ref2,
    rest = (0, _objectWithoutProperties2.default)(_ref2, _excluded2);
  const value = (_useContext = (0, _react.useContext)(SizeContext)) !== null && _useContext !== void 0 ? _useContext : 'md';
  return /*#__PURE__*/_react.default.createElement(_text.text.td, (0, _extends2.default)({
    size: ['sm', 'md', 'lg'].indexOf(value) - 1,
    className: (0, _classnames.default)('ux-td', className),
    as: "paragraph",
    "data-align": align
  }, rest), /*#__PURE__*/_react.default.createElement("div", {
    className: "ux-table-cell-container"
  }, children));
}

/**
 * Creates a Th component comprised of a html th element
 * @param {Object} props - Properties for the Th element
 * @param {String} props.id - Assigns title to component for a11y
 * @param {String} props.className - Class name applied to component
 * @param {String} props.align - Determines the alignment of content respective to its cell container
 * @param {String|Boolean} props.ariaSort - Sets aria-sort for ally
 * @returns {React.ReactComponentElement} - Th component
 */
function Th(_ref3) {
  var _useContext2;
  let {
      className,
      children,
      align = 'start',
      ariaSort = false
    } = _ref3,
    rest = (0, _objectWithoutProperties2.default)(_ref3, _excluded3);
  const value = (_useContext2 = (0, _react.useContext)(SizeContext)) !== null && _useContext2 !== void 0 ? _useContext2 : 'md';
  const adjustedAriaSort = ariaSort ? ariaSort.replace(/(asc|desc)(?:ending)?/, '$1ending') : null;
  return /*#__PURE__*/_react.default.createElement(_text.text.th, (0, _extends2.default)({
    className: (0, _classnames.default)('ux-th', className),
    "data-align": align,
    size: ['sm', 'md', 'lg'].indexOf(value) - 1,
    as: "action",
    "aria-sort": adjustedAriaSort
  }, rest), /*#__PURE__*/_react.default.createElement("div", {
    className: "ux-table-cell-container"
  }, children));
}

/**
 * Creates a Tbody component comprised of an html tbody element
 * @param {Object} props - Properties for the Tbody element
 * @param {String} props.id - Assigns title to component for a11y
 * @param {String} props.className - Class name applied to component
 * @returns {React.ReactComponentElement} - Tbody component
 */
function Tbody(_ref4) {
  let {
      className,
      children
    } = _ref4,
    rest = (0, _objectWithoutProperties2.default)(_ref4, _excluded4);
  return /*#__PURE__*/_react.default.createElement("tbody", (0, _extends2.default)({
    className: (0, _classnames.default)('ux-tbody', className)
  }, rest), children);
}

/**
 * Creates a Tr component comprised of an html tr element
 * @param {Object} props - Properties for the Tr element
 * @param {String} props.id - Assigns title to component for a11y
 * @param {String} props.className - Class name applied to component
 * @returns {React.ReactComponentElement} - Tr component
 */
function Tr(_ref5) {
  let {
      className,
      children
    } = _ref5,
    rest = (0, _objectWithoutProperties2.default)(_ref5, _excluded5);
  return /*#__PURE__*/_react.default.createElement("tr", (0, _extends2.default)({
    className: (0, _classnames.default)('ux-tr', className)
  }, rest), children);
}

/**
 * Creates a Tfoot component comprised of an html tfoot element
 * @param {Object} props - Properties for the Tfoot element
 * @param {String} props.id - Assigns title to component for a11y
 * @param {String} props.className - Class name applied to component
 * @returns {React.ReactComponentElement} - Tfoot component
 */
function Tfoot(_ref6) {
  let {
      className,
      children
    } = _ref6,
    rest = (0, _objectWithoutProperties2.default)(_ref6, _excluded6);
  return /*#__PURE__*/_react.default.createElement("tfoot", (0, _extends2.default)({
    className: (0, _classnames.default)('ux-tfoot', className)
  }, rest), children);
}

/**
 * Creates a Thead component comprised of an html thead element
 * @param {Object} props - Properties for the Thead element
 * @param {String} props.id - Assigns title to component for a11y
 * @param {String} props.className - Class name applied to component
 * @param {Boolean} props.isSticky - Determines whether the header is sticky or not, false by default
 * @returns {React.ReactComponentElement} - Thead component
 */
function Thead(_ref7) {
  let {
      className,
      children,
      isSticky = false
    } = _ref7,
    rest = (0, _objectWithoutProperties2.default)(_ref7, _excluded7);
  return /*#__PURE__*/_react.default.createElement("thead", (0, _extends2.default)({
    "data-sticky": isSticky,
    className: (0, _classnames.default)('ux-thead', className)
  }, rest), children);
}

/**
 * Creates a styled html button component that meets design and accessibility standards for interactivity
 * @param {Object} props - Properties for the button element
 * @param {String} props.id - Assigns title to component for a11y
 * @param {String} props.className - Class name applied to component
 * @returns {React.ReactComponentElement} - SortableHeader component
 */
function SortableHeader(_ref8) {
  var _useContext3;
  let {
      className,
      children
    } = _ref8,
    rest = (0, _objectWithoutProperties2.default)(_ref8, _excluded8);
  const value = (_useContext3 = (0, _react.useContext)(SizeContext)) !== null && _useContext3 !== void 0 ? _useContext3 : 'md';
  return /*#__PURE__*/_react.default.createElement(_text.text.button, (0, _extends2.default)({
    as: "action",
    className: (0, _classnames.default)('ux-sort-header-button', className),
    size: ['sm', 'md', 'lg'].indexOf(value) - 1
  }, rest), /*#__PURE__*/_react.default.createElement("div", {
    className: "ux-sort-header-button-container"
  }, children, /*#__PURE__*/_react.default.createElement(_arrowDown.default, {
    className: "ux-sort-arrow"
  })));
}
Table.propTypes = {
  /** Class name applied to component */
  className: _propTypes.default.string,
  /** Additional detail content */
  children: _propTypes.default.node,
  /** Assigns title to component for a11y */
  id: _propTypes.default.string,
  /** Determines the padding and font size of table content */
  size: _propTypes.default.oneOf(['sm', 'md', 'lg']),
  /** Determines whether the table element and its contents should stretch to fit its container */
  stretch: _propTypes.default.bool
};
Td.propTypes = {
  /** Class name applied to component */
  className: _propTypes.default.string,
  /** Additional detail content */
  children: _propTypes.default.node,
  /** Assigns title to component for a11y */
  id: _propTypes.default.string,
  /** Determines the alignment of content respective to its cell container */
  align: _propTypes.default.oneOf(['start', 'end'])
};
Th.propTypes = {
  /** Class name applied to component */
  className: _propTypes.default.string,
  /** Additional detail content */
  children: _propTypes.default.node,
  /** Assigns title to component for a11y */
  id: _propTypes.default.string,
  /** Determines the alignment of content respective to its cell container */
  align: _propTypes.default.oneOf(['start', 'end']),
  /** Sets aria-sort for ally */
  ariaSort: _propTypes.default.oneOf([false, 'asc', 'desc', 'ascending', 'descending'])
};
Tbody.propTypes = {
  /** Class name applied to component */
  className: _propTypes.default.string,
  /** Additional detail content */
  children: _propTypes.default.node,
  /** Assigns title to component for a11y */
  id: _propTypes.default.string
};
Tr.propTypes = {
  /** Class name applied to component */
  className: _propTypes.default.string,
  /** Additional detail content */
  children: _propTypes.default.node,
  /** Assigns title to component for a11y */
  id: _propTypes.default.string
};
Tfoot.propTypes = {
  /** Class name applied to component */
  className: _propTypes.default.string,
  /** Additional detail content */
  children: _propTypes.default.node,
  /** Assigns title to component for a11y */
  id: _propTypes.default.string
};
Thead.propTypes = {
  /** Class name applied to component */
  className: _propTypes.default.string,
  /** Additional detail content */
  children: _propTypes.default.node,
  /** Assigns title to component for a11y */
  id: _propTypes.default.string,
  /** Determines whether the header is sticky or not, false by default */
  isSticky: _propTypes.default.bool
};
SortableHeader.propTypes = {
  /** Class name applied to component */
  className: _propTypes.default.string,
  /** Additional detail content */
  children: _propTypes.default.node,
  /** Assigns title to component for a11y */
  id: _propTypes.default.string
};